import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import { InfoOutlineIcon, CopyIcon } from "@chakra-ui/icons";
import { Box, Text, Stack, Button, FormControl, FormLabel, Input, HStack, useToast, Flex } from "@chakra-ui/react";
import { Popover, PopoverTrigger, PopoverContent, PopoverBody, PopoverArrow } from "@chakra-ui/react";
import { useDisclosure, useClipboard } from "@chakra-ui/react";
import { MdOutlineRefresh } from "react-icons/md";

import Loader from "../../../utils/Loader";
import { callApiWithToken } from "../../../utils/http_common";
import MerchantKeyUpdateModal from "../modals/MerchantKeyUpdateModal";
import WebhookSecretUpdateModal from "../modals/WebhookSecretUpdateModal";

import ".././styles.css";

export default function IntegrationNew({ data }) {
  const [language, setLanguage] = useState("");
  const [webhookData, setWebhookData] = useState({});
  const [loading, setLoading] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [updateLoading, setUpdateLoading] = useState(false);

  const userDetails = useSelector((state) => state?.login?.userDetails);
  const canUpdateSettings = userDetails?.permissions?.includes("settings.update");
  const canRegenerateMerchantKey = userDetails?.permissions?.includes("merchant_key.update");
  const canRenerateWebhookSecretKey = userDetails?.permissions?.includes("webhook_secretkey.update");

  const toast = useToast();

  const { isOpen: isPasswordModalOpen, onOpen: onPasswordModalOpenClick, onClose: onPasswordModalClose } = useDisclosure();
  const { isOpen: isSecretModalOpen, onOpen: onSecretModalOpenClick, onClose: onSecretModalClose } = useDisclosure();
  const { onCopy, setValue } = useClipboard(webhookData.secret);
  const { onCopy: onUsernameCopy } = useClipboard(username);
  const { onCopy: onPasswordCopy } = useClipboard(password);

  const url = import.meta.env.VITE_NODE_ENV === "Sandbox" ? webhookData.url || "" : webhookData.prdUrl || "";
  useEffect(() => {
    getCustomerData();
    getDefaultBrowserLanguage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateSettings = async () => {
    const payload = {
      impersonatedOrg: "all",
      language,
      webhookUrl,
      redirectUrl,
      key: import.meta.env.VITE_NODE_ENV === "sandbox" ? "webhook.url" : "webhook.prdUrl",
    };

    if (!validateForm()) {
      setUpdateLoading(true);
      await callApiWithToken.post("/organization/update", payload);
      toast({
        title: "Updated Successfully..",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      setUpdateLoading(false);
    }
  };
  const getCustomerData = async () => {
    setLoading(true);
    const { orgResponse = {}, webhookResponse = {} } = data?.data || {};
    const { language, formattedName, merchantKey } = orgResponse;
    const { webhook = {}, redirectUrl = "" } = webhookResponse;
    setWebhookData(webhook);
    setValue(webhook.secret);
    setLanguage(language);
    setLoading(false);
    setRedirectUrl(redirectUrl);
    setWebhookUrl(webhook?.url || url);
    setUsername(formattedName);
    setPassword(merchantKey);
  };

  const getDefaultBrowserLanguage = () => {
    /* Call in case default language is not set. This picks up browser language and uses it as default */
    const browserLangComplete = navigator.language || navigator.userLanguage;
    const browserLang = browserLangComplete.split("-")[0];

    if (browserLang && typeof browserLang === "string" && browserLang.toLowerCase() === "en") {
      setLanguage("english");
    }
  };

  const validateForm = () => {
    let isError = false;

    return isError;
  };

  const handleUrlChange = (event) => {
    setWebhookUrl(event.target.value);
  };

  const handleRedirectUrlChange = (event) => {
    setRedirectUrl(event.target.value);
  };

  return loading ? (
    <Loader />
  ) : (
    <Box py={5} fontFamily={"DM Sans"}>
      <Stack spacing={2}>
        <Box bg="white" p={6} borderRadius={10} boxShadow="sm">
          <Text fontWeight={"semibold"} mb={4}>
            API Integration Settings
          </Text>
          <Stack spacing={2}>
            <Stack direction={"column"} justifyContent="space-between" flexGrow={1} borderRadius={10} maxWidth={"full"} width={"full"}>
              <Stack w="100%" gap={"5"}>
                <FormControl size="sm">
                  <FormLabel htmlFor="text" pt={2}>
                    Webhook URL
                  </FormLabel>
                  <HStack>
                    <Input
                      value={webhookUrl}
                      width={"100%"}
                      variant="outline"
                      placeholder="Enter webhook url"
                      onChange={handleUrlChange}
                      disabled={!canUpdateSettings}
                    />
                  </HStack>
                </FormControl>
                <FormControl size="sm">
                  <FormLabel htmlFor="text" pt={2}>
                    Redirect URL
                  </FormLabel>
                  <HStack>
                    <Input
                      value={redirectUrl}
                      width={"100%"}
                      variant="outline"
                      placeholder="Enter Redirect url"
                      onChange={handleRedirectUrlChange}
                      disabled={!canUpdateSettings}
                    />
                  </HStack>
                  <Box display={"flex"} justifyContent={"end"} width={"100%"}>
                    {canUpdateSettings && (
                      <Button
                        isLoading={updateLoading}
                        variant={"primary"}
                        bg={"#1A4FD6"}
                        _hover={{ bg: "#366cf5" }}
                        color="white"
                        style={{ marginTop: "20px" }}
                        onClick={updateSettings}
                        size="md"
                        width={"122px"}
                        height={"46px"}
                        rounded={"10px"}
                      >
                        Update
                      </Button>
                    )}
                  </Box>
                </FormControl>
              </Stack>
            </Stack>
          </Stack>
        </Box>

        <Box bg="white" p={6} borderRadius={10} boxShadow="sm">
          <Flex w={"100%"} justifyContent={"space-between"} alignItems={"center"}>
            <Text fontWeight={"semibold"}>Webhooks</Text>
          </Flex>
          <Flex w={{ lg: "100%" }} justifyContent={"flex-start"} alignItems={"center"}>
            <Text fontWeight={"normal"} width="100px" marginRight={"10px"}>
              Webhook Secret Key
            </Text>

            <Button display={"flex"} justifyContent={"space-between"} width={"300px"} variant="outline" marginRight={"10px"} onClick={onPasswordCopy}>
              <Text className="truncate" fontWeight={"normal"}>
                ****************************
              </Text>
            </Button>

            <Flex>
              {canRenerateWebhookSecretKey && (
                <MdOutlineRefresh
                  style={{ marginBottom: "6px", fontSize: "20px" }}
                  className="transition-all hover:rotate-180 cursor-pointer"
                  onClick={onSecretModalOpenClick}
                />
              )}
            </Flex>
          </Flex>
        </Box>

        <Box bg="white" p={6} borderRadius={10} boxShadow="sm">
          <Text fontWeight={"semibold"} mb={4}>
            API Authentication
          </Text>
          <Flex w={"100%"} justifyContent={{ md: "flex-start" }} alignItems={{ md: "flex-start" }} direction={{ md: "column" }} gap={4}>
            <Flex w={{ lg: "50%" }} justifyContent={"flex-start"} alignItems={"center"}>
              <Text fontWeight={"normal"} width="80px" marginRight={"10px"}>
                MID
              </Text>
              <Button
                display={"flex"}
                justifyContent={"space-between"}
                width={"400px"}
                variant="outline"
                marginRight={"10px"}
                rightIcon={
                  <Popover>
                    <PopoverTrigger>
                      <Button m={0} p={0} bg={"transparent"} height={"max-content"} onClick={() => onCopy(data?.data?.orgResponse?.mid)}>
                        <CopyIcon />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent width={"fit-content"}>
                      <PopoverArrow />
                      <PopoverBody>Copied</PopoverBody>
                    </PopoverContent>
                  </Popover>
                }
                onClick={() => onCopy(data?.data?.orgResponse?.mid)}
              >
                <Text fontWeight={"normal"}>{data?.data?.orgResponse?.mid || "NA"}</Text>
              </Button>
              <Popover placement="right">
                <PopoverTrigger>
                  <InfoOutlineIcon style={{ marginBottom: "6px", fontSize: "18px" }} />
                </PopoverTrigger>
                <PopoverContent>
                  <PopoverArrow />
                  <PopoverBody>"This is your default MID. You can also pass different MIDs as headers when making API requests."</PopoverBody>
                </PopoverContent>
              </Popover>
            </Flex>
            <Flex w={{ lg: "50%" }} justifyContent={"flex-start"} alignItems={"center"}>
              <Text fontWeight={"normal"} width="80px" marginRight={"10px"}>
                Username
              </Text>
              <Button
                display={"flex"}
                justifyContent={"space-between"}
                width={"400px"}
                variant="outline"
                marginRight={"10px"}
                rightIcon={
                  <Popover>
                    <PopoverTrigger>
                      <Button m={0} p={0} bg={"transparent"} height={"max-content"} onClick={() => onCopy(onUsernameCopy)}>
                        <CopyIcon />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent width={"fit-content"}>
                      <PopoverArrow />
                      <PopoverBody>Copied</PopoverBody>
                    </PopoverContent>
                  </Popover>
                }
                onClick={onUsernameCopy}
              >
                <Text fontWeight={"normal"}>{username}</Text>
              </Button>
              <Popover placement="right">
                <PopoverTrigger>
                  <InfoOutlineIcon style={{ marginBottom: "6px", fontSize: "18px" }} />
                </PopoverTrigger>
                <PopoverContent>
                  <PopoverArrow />
                  <PopoverBody>"It's your unique user name to create the authentication token for using TransFi API."</PopoverBody>
                </PopoverContent>
              </Popover>
            </Flex>
            <Flex w={{ lg: "50%" }} justifyContent={"flex-start"} alignItems={"center"}>
              <Text fontWeight={"normal"} width="80px" marginRight={"10px"}>
                Password
              </Text>

              <Button display={"flex"} justifyContent={"space-between"} width={"400px"} variant="outline" marginRight={"10px"} onClick={onPasswordCopy}>
                <Text className="truncate" fontWeight={"normal"}>
                  ****************************
                </Text>
              </Button>
              <Flex>
                {canRegenerateMerchantKey && (
                  <MdOutlineRefresh
                    style={{ marginBottom: "6px", fontSize: "20px" }}
                    className="transition-all hover:rotate-180 cursor-pointer"
                    onClick={onPasswordModalOpenClick}
                  />
                )}
              </Flex>
            </Flex>
          </Flex>
        </Box>

        <MerchantKeyUpdateModal isOpen={isPasswordModalOpen} onClose={onPasswordModalClose} />
        <WebhookSecretUpdateModal isOpen={isSecretModalOpen} onClose={onSecretModalClose} type="webhook" />
      </Stack>
    </Box>
  );
}
